import { mergeFreeGiftsAppend } from '@/lib/promotions';
import {
  BasePromotion,
  BasePromotionRewardProduct,
  BasePromotionRewardProductItem,
} from '@/types/http/promotion/common';
import { devtools } from 'zustand/middleware';
import { createWithEqualityFn } from 'zustand/traditional';

export type BaseFreeGift = {
  promotion_id?: string;
  product_group: BasePromotionRewardProduct;
  items: (BasePromotionRewardProductItem & {
    qty?: number;
    product_group_id?: string;
  })[];
};

type TPromotion = {
  selectedPromotion: BasePromotion[];
  selectedFreeGift: BaseFreeGift[];
  setSelectedPromotion: (promotion: BasePromotion[]) => void;
  removePromotion: (promoId: string) => void;
  addPromotion: (promo: BasePromotion, grandTotal: number, listPromotion?: BasePromotion[]) => void;
  applyPromotion: (promoToApply: BasePromotion | null) => void;
  applyVoucher: (voucherIds: string[], listPromotion: BasePromotion[]) => void;
  applyVoucherRewards: (
    promoRewards: BasePromotion[],
    voucherCode: string,
    isNewOverlapFalse: boolean
  ) => void;
  replacePromotion: (newPromo: BasePromotion) => void;
  removeFreeGift: (promoId: string) => void;
  syncFreeGiftPromotion: () => void;
  replaceFreeGift: (
    promoRewardId: string,
    data: BaseFreeGift[],
    mode?: 'append' | 'replace'
  ) => void;
  removeFreeGiftByItemId: (promoId: string, itemId: string) => void;
  totalDiscount: number;
  setTotalDiscount: (total: number) => void;
  clearSelectedPromotions: () => void;
  clearAllFreeGifts: () => void;
  replaceAllFreeGifts: (data: BaseFreeGift[]) => void;
};

export const usePromotionStore = createWithEqualityFn<TPromotion>()(
  devtools((set) => ({
    selectedPromotion: [],
    selectedFreeGift: [],
    setSelectedPromotion: (promotion) => set({ selectedPromotion: promotion }),
    addPromotion: (promo, grandTotal, listPromotion) =>
      set((state) => {
        let promosTiering = state.selectedPromotion.filter(
          (p): p is BasePromotion => p !== undefined && p.promotion_tier_id !== null
        );

        const promosNonTiering = state.selectedPromotion.filter(
          (p): p is BasePromotion =>
            p !== undefined && p.promotion_tier_id === null && p.minimum_spending <= grandTotal
        );

        if (listPromotion) {
          promosTiering = promosTiering
            .map((selected) => {
              const eligibleTier = listPromotion
                .filter(
                  (p) =>
                    p.promotion_id === selected.promotion_id && p.minimum_spending <= grandTotal
                )
                .sort((a, b) => b.minimum_spending - a.minimum_spending)[0];
              return eligibleTier || selected;
            })
            .filter((p): p is BasePromotion => p !== undefined);
        }

        let promoToAdd: BasePromotion | undefined = promo;
        if (listPromotion) {
          const eligiblePromo = listPromotion
            .filter(
              (p) => p.promotion_id === promo.promotion_id && p.minimum_spending <= grandTotal
            )
            .sort((a, b) => b.minimum_spending - a.minimum_spending)[0];
          if (eligiblePromo) promoToAdd = eligiblePromo;
          else promoToAdd = undefined;
        } else if (promo.minimum_spending > grandTotal) {
          promoToAdd = undefined;
        }

        let selectedPromotion = [...promosNonTiering, ...promosTiering];
        if (promoToAdd) {
          selectedPromotion = selectedPromotion.filter(
            (p) => p.promotion_id !== promoToAdd!.promotion_id
          );
          selectedPromotion.push(promoToAdd);
        }

        selectedPromotion = selectedPromotion.filter((p) => p.minimum_spending <= grandTotal);

        const selectedFreeGift = state.selectedFreeGift.filter((gift) => {
          const matchedPromo = selectedPromotion.find((p) => p.promotion_id === gift.promotion_id);
          if (!matchedPromo) return false;

          const rewards =
            matchedPromo.product_reward?.filter(
              (r) => r.reward_type === 'product' && r.rule_type === 'include'
            ) ?? [];

          if (rewards.length === 0) return false;

          const includeItems = rewards.flatMap((r) => r.reward_items ?? []);

          const hasAllProduct = includeItems.some(
            (it) => typeof it.item_name === 'string' && /all\s*product/i.test(it.item_name)
          );
          if (hasAllProduct) return true;

          const allowedGroupIds = new Set(
            includeItems
              .filter(
                (it) =>
                  (it.item_type ?? '').toLowerCase() === 'product_group' ||
                  (it.item_type ?? '').toLowerCase() === 'group'
              )
              .map((it) => it.target_id)
          );
          if (allowedGroupIds.size > 0) {
            if (allowedGroupIds.has(gift.product_group.product_group_id)) return true;
            const anyItemGroupMatch = gift.items.some(
              (it: unknown) =>
                typeof (it as { product_group_id?: string }).product_group_id === 'string' &&
                allowedGroupIds.has((it as { product_group_id: string }).product_group_id)
            );
            if (anyItemGroupMatch) return true;
          }

          const allowedProductIds = new Set(
            includeItems
              .filter((it) => (it.item_type ?? '').toLowerCase() === 'product')
              .map((it) => it.target_id)
          );
          if (allowedProductIds.size > 0) {
            const anyItemProductMatch = gift.items.some((it) => allowedProductIds.has(it.item_id));
            if (anyItemProductMatch) return true;
          }

          return true;
        });

        return {
          selectedPromotion,
          selectedFreeGift,
        };
      }),
    removePromotion: (promoId: string) =>
      set((state) => ({
        selectedPromotion: state.selectedPromotion.filter(
          (promo) => promo.promotion_id !== promoId
        ),
      })),
    applyPromotion: (promoToApply: BasePromotion | null) =>
      set((state) => {
        if (!promoToApply) return { selectedPromotion: state.selectedPromotion };

        const hasSelectedNonOverlap = state.selectedPromotion.some(
          (promo) => promo.is_overlap === false
        );
        const voucherPromos = state.selectedPromotion.filter((promo) => promo.use_voucher === true);

        const filteredFreeGift = state.selectedFreeGift.filter(
          (gift) => gift.promotion_id !== promoToApply.promotion_id
        );

        if (promoToApply.is_overlap === false || hasSelectedNonOverlap) {
          return { selectedPromotion: [promoToApply], selectedFreeGift: filteredFreeGift };
        }
        const alreadyAdded = voucherPromos.some(
          (promo) => promo.promotion_id === promoToApply.promotion_id
        );

        if (alreadyAdded)
          return { selectedPromotion: voucherPromos, selectedFreeGift: filteredFreeGift };

        return {
          selectedPromotion: [...voucherPromos, promoToApply],
          selectedFreeGift: filteredFreeGift,
        };
      }),
    applyVoucher: (voucherIds, listPromotion) =>
      set(() => {
        const selectedPromotions = listPromotion.filter((promo) =>
          voucherIds.includes(promo.promotion_id)
        );
        const selectingNonOverlap = selectedPromotions.some((promo) => promo.is_overlap === false);

        if (selectingNonOverlap) {
          return { selectedPromotion: selectedPromotions };
        }

        const overlapSelected = selectedPromotions.filter((p) => p.is_overlap === true);
        return { selectedPromotion: overlapSelected };
      }),
    applyVoucherRewards: (
      promoRewards: BasePromotion[],
      voucherCode: string,
      isNewOverlapFalse: boolean
    ) =>
      set((state) => {
        const prevFiltered = state.selectedPromotion.filter(
          (p): p is BasePromotion => p !== undefined
        );
        const hasPrevNonOverlap = prevFiltered.some((p) => p.is_overlap === false);

        const promoRewardsWithVoucher = promoRewards.map((promo) => ({
          ...promo,
          voucher_code: voucherCode,
        }));

        if (isNewOverlapFalse || hasPrevNonOverlap) {
          return { selectedPromotion: promoRewardsWithVoucher };
        }

        const merged = [...prevFiltered];
        promoRewardsWithVoucher.forEach((newPromo) => {
          const exists = merged.some((p) => p.promotion_id === newPromo.promotion_id);
          if (!exists) merged.push(newPromo);
        });

        return { selectedPromotion: merged };
      }),
    replacePromotion: (newPromo: BasePromotion) =>
      set((state) => {
        const filtered = state.selectedPromotion.filter(
          (p) => p.promotion_id !== newPromo.promotion_id
        );
        return {
          selectedPromotion: [...filtered, newPromo],
          selectedFreeGift: state.selectedFreeGift.filter(
            (gift) => gift.promotion_id !== newPromo.promotion_id
          ),
        };
      }),
    removeFreeGift: (promotionId: string) =>
      set((state) => ({
        selectedFreeGift: state.selectedFreeGift.filter(
          (gift) => gift.promotion_id !== promotionId
        ),
      })),
    syncFreeGiftPromotion: () =>
      set((state) => {
        const filtered = state.selectedFreeGift.filter((gift) =>
          state.selectedPromotion.some((promo) => promo.promotion_id === gift.promotion_id)
        );

        const isEqual =
          filtered.length === state.selectedFreeGift.length &&
          filtered.every(
            (gift, idx) => gift.promotion_id === state.selectedFreeGift[idx].promotion_id
          );

        if (isEqual) return {};

        return {
          selectedFreeGift: filtered,
        };
      }),
    replaceFreeGift: (promoRewardId: string, data: BaseFreeGift[], mode = 'replace') =>
      set((state) => {
        if (mode === 'replace') {
          const filtered = state.selectedFreeGift.filter(
            (item) => item.promotion_id !== promoRewardId
          );
          return {
            selectedFreeGift: [...filtered, ...data],
          };
        }

        const others = state.selectedFreeGift.filter((item) => item.promotion_id !== promoRewardId);
        const existingSamePromo = state.selectedFreeGift.filter(
          (item) => item.promotion_id === promoRewardId
        );

        const merged = mergeFreeGiftsAppend(existingSamePromo, data);
        return { selectedFreeGift: [...others, ...merged] };
      }),
    removeFreeGiftByItemId: (promoId: string, itemId: string) =>
      set((state) => {
        const updatedFreeGifts = state.selectedFreeGift
          .map((gift) => {
            if (gift.promotion_id !== promoId) return gift;
            return {
              ...gift,
              items: gift.items.filter((item) => item.item_id !== itemId),
            };
          })
          .filter((gift) => gift.items.length > 0);

        return { selectedFreeGift: updatedFreeGifts };
      }),
    totalDiscount: 0,
    setTotalDiscount: (total) => set({ totalDiscount: total }),
    clearSelectedPromotions: () =>
      set({ selectedPromotion: [], selectedFreeGift: [], totalDiscount: 0 }),
    clearAllFreeGifts: () => set({ selectedFreeGift: [] }),
    replaceAllFreeGifts: (data) =>
      set(() => ({
        selectedFreeGift: data,
      })),
  }))
);
